/**
 * Lusty Chic Fit — PDP body-shape suitability module.
 * Namespace: window.LustyChicFit.ShapeSuitability (LustyChicFit reserved Phase 1).
 *
 * This file has two clearly separated halves:
 *   1. A pure, deterministic classifier (getProductShapeSuitability) — no DOM, no
 *      localStorage, no network. Exposed on the namespace exactly like
 *      LustyChicFit.Engine / LustyChicFit.SizeEngine.
 *   2. A small render controller (bottom of file) that reads the SAME embedded product
 *      JSON already serialised by snippets/lc-fit-product-match.liquid
 *      ([data-lc-fit-product-match-data]) — no second/duplicate payload is built for
 *      this section — and paints the five shape cards + adapts the CTA.
 *
 * This module never touches Fit Match (assets/lc-fit-engine.js) or the Size Engine
 * (assets/lc-size-engine.js) — it only reads the same read-only product fields those
 * already expose, and never recomputes or overrides either engine's output.
 */
window.LustyChicFit = window.LustyChicFit || {};

(function (ns) {
  'use strict';

  // ===========================================================================
  // PART 1 — PURE CLASSIFIER
  // ===========================================================================

  var SHAPES = ['apple', 'pear', 'hourglass', 'rectangle', 'inverted-triangle'];
  var SHAPE_LABELS = { apple: 'Apple', pear: 'Pear', hourglass: 'Hourglass', rectangle: 'Rectangle', 'inverted-triangle': 'Inverted Triangle' };
  var CANONICAL_SHAPES = ['Apple', 'Pear', 'Hourglass', 'Rectangle', 'Inverted Triangle'];

  var LEVEL = { ESPECIALLY: 'especially-suited', WORKS: 'works-well', DEPENDS: 'preference-dependent' };
  var LEVEL_RANK = {};
  LEVEL_RANK[LEVEL.DEPENDS] = 0;
  LEVEL_RANK[LEVEL.WORKS] = 1;
  LEVEL_RANK[LEVEL.ESPECIALLY] = 2;

  function isNonEmptyString(v) {
    return typeof v === 'string' && v.trim() !== '';
  }
  function isNonEmptyArray(v) {
    return Array.isArray(v) && v.length > 0;
  }

  // Same normalisation as assets/lc-fit-engine.js's normalizeShapeLabel: strips a
  // trailing " Shape" suffix and only accepts the five canonical names, so an inactive
  // metaobject label (e.g. "Curvy Fit") is defensively ignored here exactly as it is
  // for Fit Match — never invented as a second parsing rule.
  function normalizeShapeLabels(rawList) {
    var out = [];
    if (!isNonEmptyArray(rawList)) return out;
    for (var i = 0; i < rawList.length; i++) {
      var raw = rawList[i];
      var label = isNonEmptyString(raw) ? raw : (raw && isNonEmptyString(raw.label) ? raw.label : null);
      if (!label) continue;
      var stripped = label.replace(/\s*Shape\s*$/i, '').trim();
      if (CANONICAL_SHAPES.indexOf(stripped) !== -1) out.push(stripped);
    }
    return out;
  }

  // Part 5 baseline — per real shopify.fit STYLE VALUE, the default tier for each shape
  // before any rise/construction refinement (Part 8). Straight's floor is "works-well"
  // for every shape, per Part 8: "should rarely land on several Preference Dependent
  // ratings unless real construction data says so." Tapered/Mom/Barrel/Boyfriend/Palazzo
  // exist in the real shopify.fit taxonomy but weren't named in the Part 5 brief; they
  // are mapped here as reasoned extensions in the same spirit (relaxed/wide silhouettes
  // favour Inverted Triangle, as Wide/Flare do) — flagged in the final report, not
  // presented as verbatim brief content.
  var STYLE_BASELINE = {
    Skinny:    { apple: LEVEL.DEPENDS, pear: LEVEL.WORKS, hourglass: LEVEL.WORKS, rectangle: LEVEL.WORKS, 'inverted-triangle': LEVEL.DEPENDS },
    Slim:      { apple: LEVEL.WORKS, pear: LEVEL.WORKS, hourglass: LEVEL.WORKS, rectangle: LEVEL.WORKS, 'inverted-triangle': LEVEL.DEPENDS },
    Straight:  { apple: LEVEL.WORKS, pear: LEVEL.WORKS, hourglass: LEVEL.WORKS, rectangle: LEVEL.WORKS, 'inverted-triangle': LEVEL.WORKS },
    Tapered:   { apple: LEVEL.WORKS, pear: LEVEL.WORKS, hourglass: LEVEL.WORKS, rectangle: LEVEL.WORKS, 'inverted-triangle': LEVEL.WORKS },
    Bootcut:   { apple: LEVEL.WORKS, pear: LEVEL.ESPECIALLY, hourglass: LEVEL.ESPECIALLY, rectangle: LEVEL.ESPECIALLY, 'inverted-triangle': LEVEL.ESPECIALLY },
    Flare:     { apple: LEVEL.WORKS, pear: LEVEL.ESPECIALLY, hourglass: LEVEL.ESPECIALLY, rectangle: LEVEL.ESPECIALLY, 'inverted-triangle': LEVEL.ESPECIALLY },
    Wide:      { apple: LEVEL.WORKS, pear: LEVEL.WORKS, hourglass: LEVEL.WORKS, rectangle: LEVEL.WORKS, 'inverted-triangle': LEVEL.ESPECIALLY },
    Palazzo:   { apple: LEVEL.WORKS, pear: LEVEL.WORKS, hourglass: LEVEL.WORKS, rectangle: LEVEL.WORKS, 'inverted-triangle': LEVEL.ESPECIALLY },
    Mom:       { apple: LEVEL.WORKS, pear: LEVEL.WORKS, hourglass: LEVEL.DEPENDS, rectangle: LEVEL.WORKS, 'inverted-triangle': LEVEL.WORKS },
    Barrel:    { apple: LEVEL.WORKS, pear: LEVEL.WORKS, hourglass: LEVEL.DEPENDS, rectangle: LEVEL.WORKS, 'inverted-triangle': LEVEL.WORKS },
    Boyfriend: { apple: LEVEL.WORKS, pear: LEVEL.WORKS, hourglass: LEVEL.DEPENDS, rectangle: LEVEL.WORKS, 'inverted-triangle': LEVEL.WORKS }
  };

  function upgrade(level) {
    if (level === LEVEL.DEPENDS) return LEVEL.WORKS;
    if (level === LEVEL.WORKS) return LEVEL.ESPECIALLY;
    return level;
  }

  var VOWEL_SOUND_SHAPES = { apple: true, hourglass: true, 'inverted-triangle': true };
  function articleFor(shape) {
    return VOWEL_SOUND_SHAPES[shape] ? 'an' : 'a';
  }

  function buildReason(level, shape, evidenceParts) {
    var shapeLabel = SHAPE_LABELS[shape];
    var article = articleFor(shape);
    var evidence = evidenceParts.length ? evidenceParts.join(', ') : "this style's cut";
    if (level === LEVEL.ESPECIALLY) return 'The ' + evidence + ' works especially well with ' + article + ' ' + shapeLabel + ' shape.';
    if (level === LEVEL.WORKS) return 'The ' + evidence + ' generally works well for ' + article + ' ' + shapeLabel + ' shape.';
    return 'Fit for ' + article + ' ' + shapeLabel + ' shape depends more on your personal preference with ' + evidence + '.';
  }

  // Part 6-7 evidence hierarchy, applied per shape:
  //   1. Merchant-reviewed custom.body_shape (bodyShapes) — strongest, never demoted.
  //   2. Product construction/fit evidence (Part 8) — refinements applied to the
  //      baseline below, upgrades only, except the one documented conservative cap.
  //   3. Generic style matrix (Part 5) — the STYLE_BASELINE fallback.
  function classifyShape(shape, bodyShapes, legFit, rise, fitType, stretch) {
    // Tier 1 — merchant-reviewed evidence wins outright, regardless of what the
    // generic matrix would otherwise say.
    if (bodyShapes.indexOf(SHAPE_LABELS[shape]) !== -1) {
      return { level: LEVEL.ESPECIALLY, reason: null, evidence: 'merchant-reviewed-body-shape' };
    }

    // No structured leg-fit data at all for this product — conservative neutral
    // fallback per Part 25, never guessed as a stronger tier.
    if (!legFit.length) {
      return { level: LEVEL.DEPENDS, reason: 'Fit depends on your preferences for this style.', evidence: 'insufficient-data' };
    }

    var bestLevel = LEVEL.DEPENDS;
    var matchedStyles = [];
    for (var i = 0; i < legFit.length; i++) {
      var style = legFit[i];
      var table = STYLE_BASELINE[style];
      if (!table) continue;
      matchedStyles.push(style);
      if (LEVEL_RANK[table[shape]] > LEVEL_RANK[bestLevel]) bestLevel = table[shape];
    }
    if (!matchedStyles.length) {
      return { level: LEVEL.DEPENDS, reason: 'Fit depends on your preferences for this style.', evidence: 'unrecognised-fit-value' };
    }

    var evidenceParts = [matchedStyles.join('/').toLowerCase() + ' fit'];
    var hasSkinny = legFit.indexOf('Skinny') !== -1;
    var hasStraight = legFit.indexOf('Straight') !== -1;

    // Part 8 refinements — upgrades only.
    if (rise === 'High' && hasSkinny && (shape === 'hourglass' || shape === 'pear' || shape === 'rectangle')) {
      bestLevel = upgrade(bestLevel);
      evidenceParts.push('high rise');
    }
    if ((rise === 'High' || rise === 'Mid') && hasStraight && shape === 'apple') {
      bestLevel = upgrade(bestLevel);
      evidenceParts.push(rise.toLowerCase() + ' rise');
    }
    if (fitType === 'Sculpting' && (shape === 'hourglass' || shape === 'rectangle')) {
      bestLevel = upgrade(bestLevel);
      evidenceParts.push('sculpting construction');
    }
    if ((stretch === 'High' || stretch === 'Very High') && hasSkinny && (shape === 'hourglass' || shape === 'pear' || shape === 'rectangle')) {
      bestLevel = upgrade(bestLevel);
      evidenceParts.push('high-stretch fabric');
    }

    // Part 8 — the one documented conservative cap: very rigid/low-stretch Skinny
    // stays Preference Dependent for Pear and Apple unless custom.body_shape already
    // said otherwise (already handled by the Tier-1 check above, which returns before
    // reaching this code). Never applied once real evidence has already granted a
    // higher tier through the upgrades directly above — those only fire when stretch
    // is High/Very High, so no conflict is possible here.
    var stretchIsLowOrUnknown = !stretch || stretch === 'Low';
    if (hasSkinny && stretchIsLowOrUnknown && (shape === 'pear' || shape === 'apple')) {
      bestLevel = LEVEL.DEPENDS;
    }

    return { level: bestLevel, reason: buildReason(bestLevel, shape, evidenceParts), evidence: 'style-matrix' };
  }

  /**
   * Returns a suitability object for all five shapes, or `null` if the product has no
   * usable fit data at all (Part 25 whole-product fallback — the caller should then
   * show the quiz-only fallback message instead of a five-shape grid).
   */
  function getProductShapeSuitability(product) {
    if (!product) return null;

    var hasAnyFitData =
      isNonEmptyArray(product.bodyShapes) ||
      isNonEmptyArray(product.legFit) ||
      isNonEmptyString(product.waistRise) ||
      isNonEmptyString(product.fitType) ||
      isNonEmptyString(product.stretchLevel) ||
      isNonEmptyString(product.waistSupport) ||
      isNonEmptyString(product.tummyControl) ||
      isNonEmptyArray(product.fitGoals);
    if (!hasAnyFitData) return null;

    var bodyShapes = normalizeShapeLabels(product.bodyShapes);
    var legFit = isNonEmptyArray(product.legFit) ? product.legFit : [];
    var rise = isNonEmptyString(product.waistRise) ? product.waistRise : null;
    var fitType = isNonEmptyString(product.fitType) ? product.fitType : null;
    var stretch = isNonEmptyString(product.stretchLevel) ? product.stretchLevel : null;

    var result = {};
    for (var i = 0; i < SHAPES.length; i++) {
      var shape = SHAPES[i];
      var classification = classifyShape(shape, bodyShapes, legFit, rise, fitType, stretch);
      if (classification.evidence === 'merchant-reviewed-body-shape' && !classification.reason) {
        classification.reason = isNonEmptyString(product.bodyShapeReason)
          ? product.bodyShapeReason
          : 'Confirmed as especially suited for this shape.';
      }
      result[shape] = { level: classification.level, reason: classification.reason };
    }
    return result;
  }

  ns.ShapeSuitability = {
    SHAPES: SHAPES,
    LEVEL: LEVEL,
    getProductShapeSuitability: getProductShapeSuitability
  };

  // ===========================================================================
  // PART 2 — RENDER CONTROLLER (reads the existing product-match JSON payload;
  // never builds a second copy of the same product data)
  // ===========================================================================

  var STORAGE_KEY = 'lustychic_fit_profile';
  var TIER_LABELS = {};
  TIER_LABELS[LEVEL.ESPECIALLY] = 'Especially suited';
  TIER_LABELS[LEVEL.WORKS] = 'Works well';
  TIER_LABELS[LEVEL.DEPENDS] = 'Depends on your fit preference';

  function safeGetProfile() {
    try {
      var raw = window.localStorage.getItem(STORAGE_KEY);
      if (!raw) return null;
      var parsed = JSON.parse(raw);
      return parsed && typeof parsed === 'object' ? parsed : null;
    } catch (e) {
      return null;
    }
  }

  function hasAnyAnswer(profile) {
    if (!profile) return false;
    return !!(
      profile.body_shape ||
      profile.body_shape_unsure ||
      profile.age_range ||
      profile.height ||
      profile.usualUkSize ||
      profile.rise ||
      profile.leg_shape ||
      (profile.fit_goals && profile.fit_goals.length)
    );
  }

  function readProductData() {
    var node = document.querySelector('[data-lc-fit-product-match-data]');
    if (!node) return null;
    try {
      var data = JSON.parse(node.textContent);
      return data && typeof data === 'object' ? data : null;
    } catch (e) {
      return null;
    }
  }

  function isDebugRequested() {
    try {
      return new URLSearchParams(window.location.search).get('fitdebug') === '1';
    } catch (e) {
      return false;
    }
  }

  function renderFitGlance(root, product) {
    var el = root.querySelector('[data-lc-shape-fit-glance]');
    if (!el) return;
    var parts = [];
    if (product.waistRise) parts.push(product.waistRise + ' Rise');
    if (product.legFit && product.legFit.length) parts.push(product.legFit.join(', '));
    if (product.stretchLevel) parts.push(product.stretchLevel + ' Stretch');
    if (product.fitType) parts.push(product.fitType);
    if (parts.length) {
      el.textContent = 'Fit at a glance: ' + parts.join(' · ');
      el.hidden = false;
    }
  }

  // True only when the upper Fit Match panel is in its "invite" (no-profile) state —
  // i.e. the only state where it does NOT already render fit note + size advice itself
  // ('lowdata' and 'match' both do). Shared by both branches of init() below so neither
  // one can duplicate text the upper panel is already showing (QA fix, 2026-09-20 —
  // originally only the five-shape-grid branch checked this; the whole-product-fallback
  // branch did not, which could double-render identical fit note/size advice text for a
  // product that has those two fields populated but no other structured fit data at
  // all — not reachable by any of the 24 real active products at the time of this fix,
  // since every one of them has at least a `shopify.fit` value, but a latent duplication
  // bug for a future product shaped that way).
  function upperPanelNeedsSupportingContent() {
    var upperInvite = document.querySelector('[data-lc-fit-pm-invite]');
    return !!(upperInvite && upperInvite.hidden === false);
  }

  function renderSupportingContentIfNotShownAbove(root, product) {
    if (!upperPanelNeedsSupportingContent()) return; // upper panel already covers this product

    var fitNoteEl = root.querySelector('[data-lc-shape-fitnote]');
    var sizeAdviceEl = root.querySelector('[data-lc-shape-sizeadvice]');
    if (fitNoteEl && product.fitNote) {
      fitNoteEl.textContent = 'Fit — ' + product.fitNote;
      fitNoteEl.hidden = false;
    }
    if (sizeAdviceEl && product.sizeAdvice) {
      sizeAdviceEl.textContent = 'Size advice — ' + product.sizeAdvice;
      sizeAdviceEl.hidden = false;
    }
  }

  function renderFallbackSupportingContent(root, product) {
    if (!upperPanelNeedsSupportingContent()) return; // upper panel already covers this product

    var fbFitNote = root.querySelector('[data-lc-shape-fallback-fitnote]');
    var fbSizeAdvice = root.querySelector('[data-lc-shape-fallback-sizeadvice]');
    if (fbFitNote && product.fitNote) {
      fbFitNote.textContent = 'Fit — ' + product.fitNote;
      fbFitNote.hidden = false;
    }
    if (fbSizeAdvice && product.sizeAdvice) {
      fbSizeAdvice.textContent = 'Size advice — ' + product.sizeAdvice;
      fbSizeAdvice.hidden = false;
    }
  }

  function renderShapeCards(root, suitability) {
    SHAPES.forEach(function (shape) {
      var card = root.querySelector('[data-lc-shape-card="' + shape + '"]');
      if (!card) return;
      var entry = suitability[shape];
      card.classList.add('lc-fit-shape-suitability-card--' + entry.level);
      var tierEl = card.querySelector('[data-lc-shape-tier]');
      var reasonEl = card.querySelector('[data-lc-shape-reason]');
      if (tierEl) tierEl.textContent = TIER_LABELS[entry.level];
      if (reasonEl && entry.reason) {
        reasonEl.textContent = entry.reason;
        reasonEl.hidden = false;
      }
    });
  }

  // Parts 16-17 — CTA adapts to existing profile state, and avoids duplicating the
  // upper panel's own CTAs when that panel is already showing a Fit Match for this
  // product.
  function adaptCta(root, profile) {
    var headingEl = root.querySelector('[data-lc-shape-cta-heading]');
    var supportEl = root.querySelector('[data-lc-shape-cta-support]');
    var primaryEl = root.querySelector('[data-lc-shape-cta-primary]');
    var secondaryEl = root.querySelector('[data-lc-shape-cta-secondary]');
    var microcopyEl = root.querySelector('[data-lc-shape-cta-microcopy]');

    var upperMatchPanel = document.querySelector('[data-lc-fit-pm-match]');
    var upperMatchShowing = upperMatchPanel && upperMatchPanel.hidden === false;

    if (!hasAnyAnswer(profile)) {
      return 'no-profile'; // default markup already correct — nothing to change
    }

    if (upperMatchShowing) {
      if (headingEl) headingEl.textContent = 'Want to see how this compares to the rest of the range?';
      if (supportEl) supportEl.textContent = "You've already got a personalised Fit Match and Suggested Size for this style above.";
      if (primaryEl) {
        primaryEl.textContent = 'See All My Matches';
        primaryEl.setAttribute('href', '/pages/your-lusty-fit');
      }
      if (secondaryEl) {
        secondaryEl.textContent = 'Update My Fit';
        secondaryEl.setAttribute('href', '/pages/find-my-fit');
        secondaryEl.hidden = false;
      }
      if (microcopyEl) microcopyEl.hidden = true;
      return 'profile-matched';
    }

    if (headingEl) headingEl.textContent = 'Want a recommendation based on you?';
    if (supportEl) supportEl.textContent = "Tell us your body shape, height, usual size and fit preferences. We'll compare them with this style and the rest of the Lusty Chic range.";
    if (primaryEl) {
      primaryEl.textContent = 'See Your Personal Fit';
      primaryEl.setAttribute('href', '/pages/your-lusty-fit');
    }
    if (secondaryEl) {
      secondaryEl.textContent = 'Update My Fit';
      secondaryEl.setAttribute('href', '/pages/find-my-fit');
      secondaryEl.hidden = false;
    }
    if (microcopyEl) microcopyEl.hidden = true;
    return 'profile-general';
  }

  function renderDebugPanel(root, product, suitability, ctaState) {
    var panel = document.createElement('dl');
    panel.setAttribute('data-lc-shape-debug', '');
    panel.style.cssText = 'margin:16px 0;padding:12px;border:1px dashed #999;font:12px/1.5 monospace;max-width:100%;overflow-x:auto;';
    function row(label, value) {
      var dt = document.createElement('dt');
      dt.style.cssText = 'font-weight:bold;margin-top:6px;';
      dt.textContent = label;
      var dd = document.createElement('dd');
      dd.style.margin = '0 0 0 12px';
      dd.textContent = value;
      panel.appendChild(dt);
      panel.appendChild(dd);
    }
    var caption = document.createElement('p');
    caption.style.cssText = 'font-weight:bold;margin:0 0 4px;';
    caption.textContent = 'LC Shape Suitability debug (?fitdebug=1) — internal QA only';
    panel.appendChild(caption);
    row('ctaState', ctaState);
    row('bodyShapes (raw)', JSON.stringify(product.bodyShapes || []));
    row('legFit', JSON.stringify(product.legFit || []));
    row('waistRise', String(product.waistRise));
    row('fitType', String(product.fitType));
    row('stretchLevel', String(product.stretchLevel));
    if (suitability) {
      SHAPES.forEach(function (shape) {
        row(shape, JSON.stringify(suitability[shape]));
      });
    } else {
      row('suitability', 'null (whole-product fallback shown)');
    }
    root.appendChild(panel);
  }

  function init(root) {
    try {
      var product = readProductData();
      if (!product) return;

      var profile = safeGetProfile();
      var suitability = getProductShapeSuitability(product);
      var debug = isDebugRequested();

      var fullBlock = root.querySelector('[data-lc-shape-suitability-full]');
      var fallbackBlock = root.querySelector('[data-lc-shape-suitability-fallback]');

      if (suitability) {
        renderFitGlance(root, product);
        renderShapeCards(root, suitability);
        renderSupportingContentIfNotShownAbove(root, product);
        if (fullBlock) fullBlock.hidden = false;
      } else {
        // Part 25 — whole-product fallback: no misleading five-shape grid at all.
        renderFallbackSupportingContent(root, product);
        if (fallbackBlock) fallbackBlock.hidden = false;
      }

      var ctaState = adaptCta(root, profile);
      root.hidden = false;

      if (debug) renderDebugPanel(root, product, suitability, ctaState);
    } catch (e) {
      // Fails safe — the whole section stays hidden, the rest of the PDP is unaffected.
    }
  }

  document.addEventListener('DOMContentLoaded', function () {
    var root = document.querySelector('[data-lc-shape-suitability]');
    if (root) init(root);
  });
})(window.LustyChicFit);
